import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedVersionAtom,
    dataSourceVersionAtom,
    dataSourceJobsAtom,
} from '../store/company';
import { loadTeamUserDataMain, loadJobsClosed } from '../db/company';
import moment from 'moment';

const OLD_TABS = new Set(['CO Log', 'CO LOG']);
const NEW_TABS = new Set(['Change Order', 'Change Order Header']);

const COL = {
    both:    { bg: '#dcfce7', border: '#86efac', text: '#166534' },
    oldOnly: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
    newOnly: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
};

const emptyGroup = () => ({ jobs: new Set(), both: 0, oldOnly: 0, newOnly: 0 });

const COLogMigrationStatus = () => {
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [jobs] = useAtom(dataSourceJobsAtom);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null); // { activePending, closed, jobCount }

    const versionLabel = useMemo(() => {
        if (!versions || !selectedVersion) return '';
        const v = versions.find(v => v.Version_GUID === selectedVersion);
        if (!v) return '';
        return moment(v.Version_Start_DateTime).format('MMM D, YYYY')
            + (v.Version_Type?.includes('WIP') ? ' - WIP' : '');
    }, [versions, selectedVersion]);

    const scan = useCallback(async () => {
        if (!selectedVersion) {
            setResult(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // dataSourceJobsAtom holds active/pending jobs only. Closed jobs come
            // from a separate endpoint and aren't reliably in any atom here, so
            // fetch them fresh for this version.
            const activeJobs = (Array.isArray(jobs) ? jobs : []).filter(j => j && j.BU_ID != null);
            let closedJobs = [];
            try {
                const cj = await loadJobsClosed({ forecastGuid: selectedVersion });
                closedJobs = (cj?.data || []).filter(j => j && j.BU_ID != null);
            } catch (e) {
                console.warn('Could not load closed jobs for migration status', e);
            }

            const statusById = {};
            activeJobs.forEach(j => { statusById[j.BU_ID] = j.Job_Status || 'A'; });
            // Closed-jobs list is authoritative for status 'C'
            closedJobs.forEach(j => { statusById[j.BU_ID] = 'C'; });

            const jobIds = [...new Set([
                ...activeJobs.map(j => j.BU_ID),
                ...closedJobs.map(j => j.BU_ID),
            ])];

            if (jobIds.length === 0) {
                setResult(null);
                return;
            }

            const resp = await loadTeamUserDataMain({ ids: jobIds, forecastGuid: selectedVersion });
            const data = resp?.data || [];
            const verStr = selectedVersion.toString();

            // Collapse to one entry per (job, CO#) recording which formats it appears in
            const coMap = new Map(); // `${buId}|${coNo}` -> { buId, old, new }
            data.forEach(r => {
                if ((r.Version_GUID || '').toString() !== verStr) return;
                const tab = r.Forecast_Tab || '';
                const coNo = r.Row_Label || '';
                const buId = r.BU_ID;
                if (!coNo || buId == null) return;
                const isOld = OLD_TABS.has(tab);
                const isNew = NEW_TABS.has(tab);
                if (!isOld && !isNew) return;
                const key = `${buId}|${coNo}`;
                if (!coMap.has(key)) coMap.set(key, { buId, old: false, new: false });
                const m = coMap.get(key);
                if (isOld) m.old = true;
                if (isNew) m.new = true;
            });

            const activePending = emptyGroup();
            const closed = emptyGroup();
            coMap.forEach(co => {
                const status = statusById[co.buId];
                let g = null;
                if (status === 'A' || status === 'P') g = activePending;
                else if (status === 'C') g = closed;
                if (!g) return;
                g.jobs.add(co.buId);
                if (co.old && co.new) g.both += 1;
                else if (co.old) g.oldOnly += 1;
                else g.newOnly += 1;
            });

            setResult({ activePending, closed, jobCount: jobIds.length });
        } catch (e) {
            console.error('Migration status scan failed', e);
            setError(e?.message || 'Failed to load migration status.');
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [jobs, selectedVersion]);

    useEffect(() => { scan(); }, [scan]);

    if (!selectedVersion) {
        return (
            <View style={{ padding: 24 }}>
                <Text style={{ color: '#64748b' }}>Select a version to see migration status.</Text>
            </View>
        );
    }

    const rows = result
        ? [
            { key: 'ap', label: 'Active / Pending', g: result.activePending },
            { key: 'cl', label: 'Closed', g: result.closed },
        ]
        : [];

    const grand = result
        ? {
            jobs: new Set([...result.activePending.jobs, ...result.closed.jobs]).size,
            both: result.activePending.both + result.closed.both,
            oldOnly: result.activePending.oldOnly + result.closed.oldOnly,
            newOnly: result.activePending.newOnly + result.closed.newOnly,
        }
        : null;

    return (
        <ScrollView style={{ flex: 1, backgroundColor: '#f8fafc' }}>
            <View style={{ padding: 20 }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                    <View>
                        <Text style={{ fontSize: 20, fontWeight: '600', color: '#0f172a' }}>
                            CO Format Migration Status
                        </Text>
                        <Text style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                            Version {versionLabel || selectedVersion}
                            {result?.jobCount ? ` · ${result.jobCount} jobs scanned` : ''}
                        </Text>
                    </View>
                    <Pressable
                        onPress={() => !loading && scan()}
                        disabled={loading}
                        style={{
                            backgroundColor: loading ? '#f1f5f9' : '#fff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            padding: '8px 14px',
                            opacity: loading ? 0.6 : 1,
                        }}
                    >
                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#334155' }}>
                            {loading ? 'Scanning…' : 'Rescan'}
                        </Text>
                    </Pressable>
                </View>

                {loading ? (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: 40, alignItems: 'center' }}>
                        <ActivityIndicator size="large" color="#2563eb" />
                        <Text style={{ color: '#64748b', marginTop: 12 }}>Loading CO data across all jobs for this version&hellip;</Text>
                    </View>
                ) : error ? (
                    <View style={{ backgroundColor: '#fef2f2', borderRadius: 8, border: '1px solid #fca5a5', padding: 20 }}>
                        <Text style={{ color: '#991b1b', fontWeight: '600' }}>Could not load migration status</Text>
                        <Text style={{ color: '#b91c1c', marginTop: 4, fontSize: 13 }}>{error}</Text>
                    </View>
                ) : !result || (grand && grand.both + grand.oldOnly + grand.newOnly === 0) ? (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: 40, alignItems: 'center' }}>
                        <Text style={{ color: '#94a3b8' }}>No CO Log or Change Order data for this version.</Text>
                    </View>
                ) : (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={thStyle}>Job Group</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>Jobs w/ COs</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>Converted</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>Old format only</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>New format only</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>Total COs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const total = r.g.both + r.g.oldOnly + r.g.newOnly;
                                        return (
                                            <tr key={r.key} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                <td style={{ ...tdStyle, fontWeight: '600' }}>{r.label}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.g.jobs.size}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={r.g.both} color={COL.both} /></td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={r.g.oldOnly} color={COL.oldOnly} /></td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={r.g.newOnly} color={COL.newOnly} /></td>
                                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{total}</td>
                                            </tr>
                                        );
                                    })}
                                    {grand && (
                                        <tr style={{ borderTop: '2px solid #cbd5e1', backgroundColor: '#f1f5f9' }}>
                                            <td style={{ ...tdStyle, fontWeight: '700' }}>Total</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{grand.jobs}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={grand.both} color={COL.both} bold /></td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={grand.oldOnly} color={COL.oldOnly} bold /></td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}><Num n={grand.newOnly} color={COL.newOnly} bold /></td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{grand.both + grand.oldOnly + grand.newOnly}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </View>
                )}

                {!loading && !error && result && (
                    <View style={{ flexDirection: 'row', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                        <Legend color={COL.both} label="Converted = CO# exists in both old and new format" />
                        <Legend color={COL.oldOnly} label="Old only = not yet converted" />
                        <Legend color={COL.newOnly} label="New only = created natively in new format" />
                    </View>
                )}
            </View>
        </ScrollView>
    );
};

const Num = ({ n, color, bold }) => {
    if (!n) return <Text style={{ color: '#cbd5e1', fontWeight: bold ? '700' : '400' }}>0</Text>;
    return (
        <View style={{
            display: 'inline-flex',
            backgroundColor: color.bg,
            border: `1px solid ${color.border}`,
            borderRadius: 6,
            padding: '1px 8px',
            minWidth: 36,
            alignItems: 'center',
        }}>
            <Text style={{ color: color.text, fontWeight: bold ? '700' : '600' }}>{n}</Text>
        </View>
    );
};

const Legend = ({ color, label }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <View style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: color.bg, border: `1px solid ${color.border}` }} />
        <Text style={{ fontSize: 12, color: '#64748b' }}>{label}</Text>
    </View>
);

const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
};

const tdStyle = {
    padding: '10px 12px',
    color: '#334155',
    whiteSpace: 'nowrap',
};

export default COLogMigrationStatus;
